import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import firebaseAuthService from '../services/firebaseAuth';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import LoginOTPModal from './LoginOTPModal';
import axios, { HttpStatusCode } from 'axios';

const BG_COLOR = '#E6F0FF';
const ACCENT = '#1F4ECF';
const CARD_W = 370;
const CARD_H = 438;

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [otpError, setOtpError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await firebaseAuthService.login(email, password);

      if (result.success) {
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));
        const userData = userDoc.data();

        // ✅ Check if user has 2FA enabled (using snake_case field)
        if (userData?.two_factor_enabled) {
          // Get token from Firebase auth.currentUser directly
          const idToken = await auth.currentUser?.getIdToken();

          if (!idToken) {
            setError('Failed to authenticate. Please try again.');
            setLoading(false);
            return;
          }

          // Send OTP request to backend
          try {
            const otpResponse = await axios.post(
              `${process.env.REACT_APP_API_BASE_URL}/2fa/send-login-code`,
              {},
              {
                headers: {
                  'Authorization': `Bearer ${idToken}`,
                  'Content-Type': 'application/json'
                }
              }
            );

            if (otpResponse.data.status === HttpStatusCode.Created){
              // ✅ Store OTP token from response
              if (otpResponse.data.data?.otp_token) {
                setOtpToken(otpResponse.data.data.otp_token);
                console.log('Login OTP token received');
              } else {
                setError('Failed to receive verification token. Please try again.');
                setLoading(false);
                return;
              }

              // Store user data and show OTP modal
              setPendingUser({ ...result, userData });
              setOtpEmail(userData.email || email);
              setOtpError(''); // ✅ Clear previous OTP errors
              setShowOTPModal(true);
              setLoading(false);
              return;
            }
          } catch (otpError) {
            console.error('Failed to send OTP:', otpError);
            setError(otpError.response?.data?.message || 'Failed to send verification code. Please try again.');
            setLoading(false);
            return;
          }
        }else{
          // If no 2FA, proceed with normal login
          completeLogin(userData, result.user);
        }

      } else {
        setError(result.error || 'Invalid credentials');
      }
    } catch (err) {
      setError('Login failed: ' + (err?.message || String(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (code) => {
    setOtpLoading(true);
    setOtpError(''); // ✅ Clear previous errors

    try {
      // Get token from Firebase auth.currentUser directly
      const idToken = await auth.currentUser?.getIdToken();

      if (!idToken) {
        setOtpError('Session expired. Please login again.');
        setOtpLoading(false);
        handleOTPModalClose();
        return;
      }

      // ✅ Validate OTP token exists
      if (!otpToken) {
        setOtpError('Session expired. Please login again.');
        setOtpLoading(false);
        handleOTPModalClose();
        return;
      }

      // ✅ Verify OTP with backend (include otp_token)
      const verifyResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/2fa/verify-login`,
        { 
          code,
          otp_token: otpToken
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (verifyResponse.data.status === 200) {
        // OTP verified successfully - complete login
        setShowOTPModal(false);
        setOtpToken('');
        completeLogin(pendingUser.userData, pendingUser.user);
      } else {
        // ✅ Set error in OTP modal state
        setOtpError(verifyResponse.data.message || 'Invalid verification code');
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      // ✅ Set error in OTP modal state
      setOtpError(err.response?.data?.message || 'Invalid verification code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const completeLogin = (userData, user) => {
    if (userData?.role === 'admin') {
      navigate('/admin', { replace: true });
      return;
    }

    const from = location.state?.from;
    const normalizedFrom = typeof from === 'string' ? from : undefined;
    const redirectTo =
      normalizedFrom && normalizedFrom !== '/' && normalizedFrom !== '/login'
        ? normalizedFrom
        : '/dashboard';

    navigate(redirectTo, { replace: true });
  };

  const handleOTPModalClose = async () => {
    setShowOTPModal(false);
    setPendingUser(null);
    setOtpToken('');
    setOtpError(''); // ✅ Clear OTP errors
    // Sign out from Firebase since login is not complete
    await firebaseAuthService.logout();
  };

  return (
    <>
      <div className="relative w-screen h-screen overflow-hidden" style={{ backgroundColor: BG_COLOR }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: "url('/bg.svg')",
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: 'full',
            opacity: 0.3,
            zIndex: 0,
          }}
        />

        <div
          style={{
            position: 'absolute',
            top: 24,
            left: 32,
            zIndex: 2,
          }}
        >
          <img src="/bidz-logo.svg" alt="BidZ" style={{ height: 40 }} />
        </div>

        <div className="flex items-center justify-center w-full h-full relative z-10">
          <div
            role="region"
            aria-label="Login"
            style={{
              width: CARD_W,
              height: CARD_H,
              position: 'relative',
              borderRadius: 12,
              background: 'rgba(255,255,255,0.98)',
              boxShadow: '0 20px 40px rgba(16,40,80,0.18)',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 30,
                left: 0,
                right: 0,
                textAlign: 'center',
                fontSize: 24,
                fontWeight: 700,
              }}
            >
              Login
            </div>

            <div style={{ position: 'absolute', left: 35, top: 96, width: 300 }}>
              <label style={{ fontSize: 14, fontWeight: 500 }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="i.e. johndoe@gmail.com"
                className='focus:border-2  border-primary-blue'
                style={{
                  width: '100%',
                  height: 50,
                  marginTop: 6,
                  outline: 'none',
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1.5px solid ${ACCENT}`,
                }}
              />
            </div>

            <div style={{ position: 'absolute', left: 35, top: 196, width: 300 }}>
              <label style={{ fontSize: 14, fontWeight: 500 }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  style={{
                    width: '100%',
                    height: 50,
                    marginTop: 6,
                    padding: '0 40px 0 15px',
                    borderRadius: 8,
                    border: `1.5px solid ${ACCENT}`,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />

                <img
                  src="/eye-icon.svg"
                  alt="Show password"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '55%',
                    transform: 'translateY(-50%)',
                    width: 20,
                    height: 20,
                    cursor: 'pointer',
                    opacity: showPassword ? 1 : 0.5,
                  }}
                />
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                position: 'absolute',
                left: 35,
                top: 300,
                width: 300,
                height: 56,
                backgroundColor: ACCENT,
                color: '#fff',
                borderRadius: 10,
                border: 'none',
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {loading ? 'Logging in...' : 'LOGIN'}
            </button>

            {error && (
              <div
                style={{
                  position: 'absolute',
                  left: 20,
                  right: 20,
                  bottom: 60,
                  background: '#FEE2E2',
                  color: '#991B1B',
                  padding: 8,
                  borderRadius: 6,
                  textAlign: 'center',
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}

            <div
              style={{
                position: 'absolute',
                bottom: 20,
                width: '100%',
                textAlign: 'center',
                fontSize: 14,
              }}
            >
              Don't have an account?{' '}
              <button
                onClick={() => navigate('/register')}
                style={{
                  color: ACCENT,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Register
              </button>
            </div>
          </div>
        </div>
      </div>

      <LoginOTPModal
        isOpen={showOTPModal}
        onClose={handleOTPModalClose}
        onVerify={handleOTPVerify}
        email={otpEmail}
        loading={otpLoading}
        otpToken={otpToken}
        externalError={otpError} 
      />
    </>
  );
};

export default LoginForm;
